import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import useAccountInfo from "@/hooks/useAccountInfo";
import { api } from "@/lib/api-client";
import type { CardPaymentFailure } from "@/server-types";
import { canAccessClientBilling } from "@/utils/billing-access";
import {
  describeCardFailureReason,
  earliestRetryAt,
  formatFailureDate,
  formatFailureTime,
  latestCollectionEnd,
  notifyRetryResult,
} from "@/utils/card-payment-failure";
import { Button } from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";
import { useNavigate } from "react-router";

/**
 * Shown instead of the overdue banner while a card payment is failing, so the
 * salon can fix it before the invoice goes to bank transfer. Dismiss is
 * in-memory, like the overdue banner.
 */
export function CardPaymentFailedBanner({
  failures,
}: {
  failures: CardPaymentFailure[];
}) {
  const navigate = useNavigate();
  const accountInfo = useAccountInfo((state) => state.accountInfo);
  const [dismissed, setDismissed] = useState(false);
  const { mutateAsync: retryCharge, isPending: isRetrying } =
    api.billing.useRetryCardCharge();

  const [oldest] = failures;
  if (dismissed || !oldest) return null;

  const canOpenBilling = canAccessClientBilling(accountInfo);
  const retryAt = earliestRetryAt(failures);
  const collectionEndsAt = latestCollectionEnd(failures);
  const transferDate = collectionEndsAt && formatFailureDate(collectionEndsAt);
  const retryTime = retryAt && formatFailureTime(retryAt);

  return (
    <div
      className="sticky top-0 z-40 w-full"
      data-identifier="card-payment-failed-banner"
    >
      <LimeAlert
        color="danger"
        classNames={{ base: "rounded-none border-x-0 border-t-0" }}
        isClosable
        onClose={() => {
          setDismissed(true);
        }}
        title={<Trans>Plačilo s kartico ni uspelo</Trans>}
        description={
          <>
            {describeCardFailureReason(oldest.reason)}{" "}
            {transferDate && (
              <Trans>
                Če plačilo do {transferDate} ne uspe, vam bomo račun poslali v
                plačilo z nakazilom.
              </Trans>
            )}
          </>
        }
        endContent={
          canOpenBilling ? (
            <div className="flex shrink-0 gap-2">
              {retryAt && (
                <Button
                  color="danger"
                  data-identifier="card-payment-retry"
                  isDisabled={retryAt.getTime() > Date.now()}
                  isLoading={isRetrying}
                  size="sm"
                  variant="flat"
                  onPress={async () => {
                    notifyRetryResult(await retryCharge());
                  }}
                >
                  {retryAt.getTime() > Date.now() ? (
                    <Trans>Poskusi znova ob {retryTime}</Trans>
                  ) : (
                    <Trans>Poskusi znova</Trans>
                  )}
                </Button>
              )}
              <Button
                color="danger"
                data-identifier="card-payment-replace-card"
                size="sm"
                onPress={() => {
                  navigate("/dashboard/billing?addCard=true");
                }}
              >
                <Trans>Zamenjaj kartico</Trans>
              </Button>
            </div>
          ) : (
            <span className="text-foreground-500 shrink-0 text-xs">
              <Trans>Za poravnavo kontaktirajte skrbnika računa.</Trans>
            </span>
          )
        }
      />
    </div>
  );
}
